import { createTheme } from "@mui/material";
import { deepmerge } from "@mui/utils";
import { MUITpThemeOptions, tpTheme } from "@trainingpeaks/designtokens";
import type { ThemeOptions } from "@mui/material/styles";

const customTheme = {
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          // MUI's Button root explicitly sets borderRadius: theme.shape.borderRadius (4px),
          // which overrides the 64px borderRadius in MUITpThemeOptions.typography.button.
          // Re-applying it here as a styleOverride wins the cascade and restores the
          // rounded pill shape the design tokens intend.
          borderRadius: tpTheme.baseLayer.borderRadius.xl,
        },
      },
    },
    MuiAppBar: {
      variants: [
        {
          props: { variant: "topNav" as const },
          style: {
            backgroundColor: tpTheme.semanticLayer.colors.activity.totalAccent,
            boxShadow: "none",
          },
        },
        {
          props: { variant: "sideNav" as const },
          style: {
            backgroundColor: tpTheme.semanticLayer.colors.fff.formContrast,
            borderRight: `1px solid ${tpTheme.semanticLayer.colors.divider.main}`,
            boxShadow: "none",
          },
        },
      ],
    },
    MuiListItemButton: {
      variants: [
        {
          props: { variant: "sideNav" as const },
          style: {
            textTransform: "none",
            "&:hover": {
              borderRight: `3px solid ${tpTheme.semanticLayer.colors.primary.main}`,
              borderRadius: 0,
              backgroundColor: "transparent",
              color: tpTheme.semanticLayer.colors.primary.main,
            },
            "&.Mui-selected": {
              borderRight: `3px solid ${tpTheme.semanticLayer.colors.primary.main}`,
              borderRadius: 0,
              backgroundColor: "transparent",
              color: tpTheme.semanticLayer.colors.primary.main,
            },
            "&.Mui-selected:hover": {
              backgroundColor: "transparent",
            },
            color: tpTheme.semanticLayer.colors.text.primary,
          },
        },
      ],
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tpTheme.semanticLayer.colors.fff.formContrast,
          width: 220,
          boxSizing: "border-box",
          paddingTop: "56px",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${tpTheme.semanticLayer.colors.divider.light}`,
          borderRadius: tpTheme.baseLayer.borderRadius.lg,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tpTheme.baseLayer.borderRadius.xl,
          border: `1px solid ${tpTheme.semanticLayer.colors.divider.light}`,
          boxShadow: "none",
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { variant: "statusActive" as const },
          style: {
            backgroundColor: tpTheme.semanticLayer.colors.success.light,
            color: tpTheme.semanticLayer.colors.success.dark,
            fontWeight: 600,
            fontSize: "0.75rem",
          },
        },
        {
          props: { variant: "statusInactive" as const },
          style: {
            backgroundColor: tpTheme.semanticLayer.colors.divider.light,
            color: tpTheme.semanticLayer.colors.text.secondary,
            fontWeight: 600,
            fontSize: "0.75rem",
          },
        },
      ],
    },
    MuiLink: {
      variants: [
        {
          props: { variant: "topNav" as const },
          style: {
            "&:hover": {
              borderBottom: `4px solid ${tpTheme.semanticLayer.colors.primary.main}`,
              color: tpTheme.semanticLayer.colors.fff.formContrast,
              borderRadius: 0,
              paddingBottom: 4,
            },
            borderRadius: 0,
            color: tpTheme.baseLayer.colors.coolGray[600],
          },
        },
        {
          props: { variant: "topNavSelected" as const },
          style: {
            borderBottom: `4px solid ${tpTheme.semanticLayer.colors.primary.main}`,
            borderRadius: 0,
            color: tpTheme.semanticLayer.colors.fff.formContrast,
          },
        },
      ],
    },
  },
};

const mergedThemeOptions = deepmerge(
  MUITpThemeOptions,
  customTheme
) as ThemeOptions;

export const billingTheme = createTheme(mergedThemeOptions);
